import { StrictMode } from "react";
import { createRoot } from "react-dom/client";
import { HashRouter, useLocation } from "react-router-dom";
import { ErrorBoundary } from "react-error-boundary";
import { AppContainer, ErrorRender } from "@lark-apaas/client-toolkit-lite";
import { ActiveWorkoutProvider } from "@/hooks/useActiveWorkout";
import { DailyHealthProvider } from "@/hooks/useDailyHealth";
import VersionChecker from "@/components/VersionChecker";
import UserIdentityGuard from "@/components/UserIdentityGuard";
import App from "./app";
import "./index.css";

/**
 * 带路由感知的 ErrorBoundary 壳。
 *
 * 关键：每次 pathname 变化时更新 `resetKey`，
 * 触发 ErrorBoundary 自动 reset —— 即使上一个页面把 ErrorBoundary 撞进了错误态，
 * 切到新路由也能重新渲染，不会一直锁死在白屏错误页。
 *
 * 为什么需要这个：
 * - 任一页面的渲染错误（undefined 访问、require 残留等）都会让 ErrorBoundary 进入错误态
 * - ErrorBoundary 默认需要手动 reset 才能恢复
 * - 用户场景：点到一个坏页面 → 白屏 → 点其他 Tab 也还是白屏 → 以为"Tab 点了没反应"
 * - 加 key 后：切 Tab = key 变 = ErrorBoundary 自动重挂载 = 恢复渲染
 */
function ErrorBoundaryWithRouteReset({ children }: { children: React.ReactNode }) {
  const location = useLocation();
  return (
    <ErrorBoundary
      key={location.pathname}
      fallbackRender={({ error, resetErrorBoundary }) => (
        <ErrorRender error={error} resetErrorBoundary={resetErrorBoundary} />
      )}
    >
      {children}
    </ErrorBoundary>
  );
}

createRoot(document.getElementById("root")!).render(
  <StrictMode>
    <HashRouter>
      <AppContainer>
         <ActiveWorkoutProvider>
           <DailyHealthProvider>
             <ErrorBoundaryWithRouteReset>
               <App />
             </ErrorBoundaryWithRouteReset>
           </DailyHealthProvider>
           <VersionChecker />
           <UserIdentityGuard />
         </ActiveWorkoutProvider>
      </AppContainer>
    </HashRouter>
  </StrictMode>,
);
